import React, { useEffect, useState } from 'react';
import * as S from '../../../../../components/styles/modal-style';
import { Overlay, ErrorText, InfoText } from './QuickModal.style';
import { FormGroup, Label, Input } from '../style';
import ApiService from '../../../../../services/ApiService';
import { formatPhone } from '../../../../../utils/masks';

export interface CompanyContact {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
}

interface QuickContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
  onSaved: (contact: CompanyContact) => void;
}

/**
 * Cadastro de contato a partir da venda: cria o registro no cadastro da empresa
 * (POST pontual — não passa pelo update full-replace) e devolve o contato criado
 * para seleção imediata no formulário.
 */
const QuickContactModal: React.FC<QuickContactModalProps> = ({
  isOpen, onClose, companyId, companyName, onSaved,
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setEmail('');
    setPhone('');
    setError('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setError('');
    if (!name.trim()) {
      setError('Informe o nome do contato.');
      return;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length > 0 && phoneDigits.length < 10) {
      setError('Telefone incompleto. Informe DDD e número com pelo menos 10 dígitos.');
      return;
    }
    setSaving(true);
    try {
      const response = await ApiService.companies.createContact(companyId, {
        name: name.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
      });
      onSaved(response.data as CompanyContact);
      onClose();
    } catch (e: any) {
      const message = e?.response?.data?.message;
      setError(Array.isArray(message) ? message.join(' ') : (message || 'Erro ao salvar. Tente novamente.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <S.ContainerOfModal width="480px" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <S.Header>
          <S.HeaderTitle>Novo contato</S.HeaderTitle>
        </S.Header>
        <InfoText>
          O contato será criado no cadastro de <strong>{companyName}</strong>.
        </InfoText>
        <FormGroup>
          <Label htmlFor="quickContactName">Nome</Label>
          <Input
            id="quickContactName"
            autoComplete="off"
            placeholder="Nome do contato"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="quickContactEmail">Email</Label>
          <Input
            id="quickContactEmail"
            type="email"
            autoComplete="off"
            placeholder="email@exemplo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="quickContactPhone">Telefone</Label>
          <Input
            id="quickContactPhone"
            autoComplete="off"
            placeholder="(00) 00000-0000"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            maxLength={15}
          />
        </FormGroup>
        {error && <ErrorText>{error}</ErrorText>}
        <S.BottomDiv>
          <S.ButtonContainer>
            <S.Button type="button" onClick={onClose} disabled={saving}>Cancelar</S.Button>
            <S.Button type="button" onClick={handleConfirm} disabled={saving || !name.trim()}>
              {saving ? 'Salvando…' : 'Salvar contato'}
            </S.Button>
          </S.ButtonContainer>
        </S.BottomDiv>
      </S.ContainerOfModal>
    </Overlay>
  );
};

export default QuickContactModal;
